import { uniqueID, withCommas } from '@common/Utils';
import { appConfig } from '@common/config';
import { numToKor } from '@common/numToKor';
import { useSetSearchParams } from '@hook/useSetSearchParams';
import { type IStockItem } from '@interface/IItemPrice';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

export const PriceItemStockItem = ({ it }: { it: IStockItem }) => {
    const { t } = useTranslation();
    const numChangeFn = numChangeFnMap[appConfig.service];
    const placeholderStr = t(placeholderStrMap[appConfig.service]);
    const [options, optionsSet] = useState<JSX.Element[]>([]);
    const setParams = useSetSearchParams();

    useEffect(() => optionsSet(MakeOptions(it)), [it]);

    return (
        <li className={classNames({ 'has-option': options.length > 0 })}>
            <span className="tradeableitems-name">
                <button className="btn-item" onClick={() => setParams({ detail: `item_${it.itemId}_${it.enchantLevel}_${it.serverId}` })}>
                    <strong
                        className="name"
                        data-itemgrade={it.grade}
                        data-detailshow={it.itemId.toString()}
                        data-enchant={it.enchantLevel.toString()}
                        data-svrid={it.serverId}
                        data-areas="시세_리스트_매물이름_클릭"
                    >
                        {it.itemName}
                        {it.blessingLevel > 0 && ` (${placeholderStr.replace('{N}', it.blessingLevel.toString())})`}
                        {it.quantity > 1 && <span className="qty">({numChangeFn(it.quantity)})</span>}
                    </strong>
                </button>
            </span>
            {options.length > 0 && (
                <span className="tradeableitems-option" style={{ display: 'block' }}>
                    {options}
                </span>
            )}
            <span className="tradeableitems-price">
                {it.world && <i className="ico-world" />}
                <i className="ico-price" />
                <strong className="price">{numChangeFn(it.salePrice)}</strong>
            </span>
        </li>
    );
};

const numChangeFnMap: Record<string, (num: number | undefined) => string> = {
    l2m: withCommas,
    lms: numToKor,
};
const placeholderStrMap: Record<string, string> = {
    l2m: 'price.divinitystage2',
    lms: 'price.divinitystage',
};

const MakeOptions = (it: IStockItem) => {
    const opArr: JSX.Element[] = [];

    if (it.blessingOption) {
        const tooltipId = `my-tooltip-${uniqueID('')}-${it.itemId}`;
        opArr.push(
            <>
                <strong style={itemStyle} data-tooltip-id={tooltipId}>
                    {it.blessingOption}
                </strong>
                <Tooltip
                    disableStyleInjection={true}
                    offset={5}
                    id={tooltipId}
                    html={it.blessingOptionDescription.replace(/\n/gi, '<br/>')}
                />
            </>
        );
    }

    if (it.soulgemEffects?.length > 0) {
        it.soulgemEffects.map((it, idx) => {
            const tooltipId = `my-tooltip-${uniqueID('')}-${it.soulgemName + idx}`;
            const soulgemOption = it.soulgemSkillOptionName
                ? `<br><br>[${it.soulgemSkillOptionName}]<br>${it.soulgemSkillOptionDescription}`
                : '';

            opArr.push(
                <>
                    <strong style={itemStyle} data-tooltip-id={tooltipId}>
                        {it.soulgemName}
                    </strong>
                    <Tooltip
                        disableStyleInjection={true}
                        offset={5}
                        id={tooltipId}
                        html={it.soulgemStatOption.map((op) => op).join('<br>') + soulgemOption}
                    />
                </>
            );
            return false;
        });
    }

    if (it.refinements?.length > 0) {
        it.refinements.map((it) => opArr.push(<span style={{ display: 'inline-block' }}>{it}</span>));
    }

    const newArray: JSX.Element[] = [];
    opArr.forEach((item, index) => {
        newArray.push(item);
        if (index !== opArr.length - 1) {
            newArray.push(<>,&nbsp;</>);
        }
    });

    return newArray;
};

const itemStyle = {
    display: 'inline-block',
    cursor: 'context-menu',
};
